import type {
  InventoryResource,
  Snapshot,
  SourceAccount,
} from '@repo/api-gw-sdk';
import {
  AccountState,
  EFSRestoreDestinationRestoreTypeEnum,
  Provider,
} from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { EFSConfiguration } from './steps/efsConfiguration';
import { EFSRestoreTypeSelection } from './steps/efsRestoreTypeSelection';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreEFSState {
  resourceId: string;
  snapshot: Snapshot;
  fileSystemName?: string;
  supportedCloudProviders: Provider[];
  restoreAccount?: SourceAccount;
  accounts?: SourceAccount[];
  emptyMessage?: string;
  regionName?: string;
  subnetIds: string[];
  securityGroupIds: string[];
  vpc?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
  performanceMode?: string;
  throughputMode?: string;
  provisionedThroughputMibps?: number;
  restoreType: EFSRestoreDestinationRestoreTypeEnum;
  itemsToRestore: string[];
  restoreToNewFileSystem: boolean;
}

const restoreEFSFlow: Step<RestoreEFSState> = {
  id: 'efs_restore_type',
  Component: EFSRestoreTypeSelection,
  next: [
    {
      id: 'efs_configuration',
      Component: EFSConfiguration,
    },
  ],
};

export const RestoreEFSWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { sourceAccounts } = useSourceAccounts();
  const dal = useDAL();
  const { data: restoreAccountsResponse } = dal.cloudAccounts.restore.useList();

  // EFS backups are stored in AWS Backup Vault in the source account
  // Only show the source account (no cross-account restore support)
  const sourceAccount = sourceAccounts?.find(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.sourceAccountAttributes.cloudProvider === Provider.Aws &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  // EFS restore requires a restore account onboarded for the same AWS account
  const hasRestoreAccount = restoreAccountsResponse?.accounts?.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  // Filter accounts to only show the source account
  const allowedAccounts =
    sourceAccount && hasRestoreAccount ? [sourceAccount] : [];

  // Wait for accounts data to load before rendering the wizard
  if (!sourceAccounts || !restoreAccountsResponse) {
    return <Loader />;
  }

  // Generate a default file system name based on the original resource name
  const defaultFileSystemName = resource.resourceName
    ? `${resource.resourceName}-restored`
    : `efs-restored-${new Date().toISOString().slice(0, 10)}`;

  return (
    <Wizard<RestoreEFSState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreEFSFlow]}
      initialState={{
        initialRegionName: resource.region || 'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        fileSystemName: defaultFileSystemName,
        supportedCloudProviders: [Provider.Aws],
        restoreAccount: sourceAccount,
        accounts: allowedAccounts,
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        subnetIds: [],
        securityGroupIds: [],
        restoreType: EFSRestoreDestinationRestoreTypeEnum.FullRestore,
        itemsToRestore: [],
        restoreToNewFileSystem: true,
        emptyMessage: !sourceAccount
          ? 'The source account for this EFS file system is not connected. EFS backups are stored in AWS Backup Vault in the source account and can only be restored to the same account.'
          : !hasRestoreAccount
            ? `A restore account is required for the same AWS account (${resource.providerAccountId}). Please onboard a restore account for this AWS account.`
            : undefined,
      }}
      title='Restore EFS'
      stepperLabels={['Restore Type', 'Configuration']}
    />
  );
};
